/**
 * @description Event types for HTML elements
 * @author Stan Gletski
 */
export enum ElementEvent {

	/**
	 * @description Native browser event type=
	 * <p>Triggered by an element when the mouse comes over it. The only difference from 'mouseover' is
	 * that the onmouseenter event does not bubble (does not propagate up the document hierarchy)</p>
	 */
	MOUSE_ENTER = 'mouseenter',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by an element when the mouse comes over it</p>
	 */
	MOUSE_OVER = 'mouseover',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by an element when the mouse goes out of it</p>
	 * @type {String}
	 */
	MOUSE_OUT = 'mouseout',

	/**
	 * @description Native browser event type=
	 * <p>The mouseleave event is fired when the pointer of a pointing device (usually a mouse) is moved out of an element.</p>
	 */
	MOUSE_LEAVE = 'mouseleave',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by an element when a mouse button is pressed down over it</p>
	 */
	MOUSE_DOWN = 'mousedown',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by an element when a mouse button is released over it</p>
	 */
	MOUSE_UP = 'mouseup',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by a mouse click= mousedown and then mouseup over an element</p>
	 */
	MOUSE_CLICK = 'click',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by two clicks within a short time over an element</p>
	 */
	MOUSE_DOUBLE_CLICK = 'dblclick',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by an element on every mouse move over it</p>
	 */
	MOUSE_MOVE = 'mousemove',

	/**
	 * @description Native browser event type=
	 * <p>Triggered by a right-button mouse click over an element</p>
	 */
	MOUSE_CONTEXT_MENU = 'contextmenu',

	/**
	 * @description Native browser event type=
	 * <p>Sent when the user places a touch point on the touch surface.</p>
	 */
	TOUCH_START = 'touchstart',

	/**
	 * @description Native browser event type=
	 * <p>Sent when the user removes a touch point from the surface
	 * (that is, when they lift a finger or stylus from the surface).
	 * This is also sent if the touch point moves off the edge of the surface;
	 * for example, if the user's finger slides off the edge of the screen.</p>
	 */
	TOUCH_END = 'touchend',

	/**
	 * @description Native browser event type=
	 * <p>Sent when a touch point has been disrupted in some way.
	 * There are several possible reasons why this might happen
	 * (and the exact reasons will vary from device to device, as well as browser to browser)</p>
	 */
	TOUCH_CANCEL = 'touchcancel',

	/**
	 * @description Native browser event type=
	 * <p>Sent when the user moves a touch point along the surface.
	 * The event's target is the same element that received the touchstart event
	 * corresponding to the touch point, even if the touch point has moved outside that element.</p>
	 */
	TOUCH_MOVE = 'touchmove',

	/**
	 * @description Native browser event type=
	 * <p>The onwheel event occurs when the mouse wheel is rolled up or down over an element.</p>
	 * <p>The onwheel event also occurs when the user scrolls or zooms in or out of an element by using a touchpad (like the "mouse" of a laptop).</p>
	 */
	ON_WHEEL = 'wheel',

	/**
	 * @description Native browser event type=
	 *  <p>fires when the user attempts to open a context menu.
	 *  This event is typically triggered by clicking the right mouse button,
	 *  or by pressing the context menu key. In the latter case,
	 *  the context menu is displayed at the bottom left of the focused element,
	 *  unless the element is a tree, in which case the context menu is displayed at the bottom left of the current row.</p>
	 */
	ON_CONTEXT_MENU = 'contextmenu',

	/**
	 * detect a scroll event
	 * @type {ElementEvent.SCROLL}
	 */
	SCROLL = 'scroll',

	/**
	 * @description The change event is fired for <input>, <select>, and <textarea> elements when the user modifies the element's value
	 * @type {ElementEvent.CHANGE}
	 */
	CHANGE = 'change',

	/**
	 * @description The input event fires when the value of an <input>, <select>, or <textarea> element has been changed
	 * as a direct result of a user action (such as typing in a textbox or checking a checkbox).
	 * @type {ElementEvent.INPUT}
	 */
	INPUT = 'input',

	/**
	 * @description The blur event fires when an element has lost focus.
	 * @type {ElementEvent.BLUR}
	 */
	BLUR = 'blur'

}

/**
 * @description Array of events for bulk subscription
 * @return {string[]}
 */
export const elementEvents = (): string[] => {
	const out: string[] = [];
	Object.keys( ElementEvent ).forEach(
		( key ) => {
			out.push( ElementEvent[ key ] );
		}
	);
	return out;
};